import { fromUnixTimestamp } from '@utils/date-manipulation/from-unix-timestamp';

import type { FormatDateTimeOptions } from './date-time-format.type';

/**
 * Moment.js format tokens mapped to Intl.DateTimeFormat options
 */
const MOMENT_FORMAT_TOKENS: Record<string, Intl.DateTimeFormatOptions> = {
  // Date formats
  'ddd, MMMM D, YYYY': { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' },
  'ddd DD MMM YY': { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' },
  'ddd DD MMM': { weekday: 'short', day: '2-digit', month: 'short' },
  'ddd DD': { weekday: 'short', day: '2-digit' },
  'MMMM YY': { month: 'long', year: 'numeric' },
  'MMM Do, HH:mm': { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },

  // Single tokens
  D: { day: 'numeric' }, // 1, 2... 31
  DD: { day: '2-digit' }, // 01, 02... 31
  dd: { weekday: 'short' }, // Su, Mo... (Intl 'short' is Sun, Mon) - format-date.ts uses charAt(0) so this is fine
  ddd: { weekday: 'short' }, // Sun, Mon...
  MMM: { month: 'short' }, // Jan, Feb...
  MMMM: { month: 'long' }, // January, February...
  YY: { year: '2-digit' }, // 70, 71... 29
  YYYY: { year: 'numeric' }, // 1970, 1971... 2029

  // Moment's localized formats
  LL: { dateStyle: 'long' }, // e.g., "January 29, 2026"
  LLL: { dateStyle: 'long', timeStyle: 'short' }, // e.g., "January 29, 2026, 10:30 AM"
  'HH:mm': { hour: '2-digit', minute: '2-digit', hour12: false },
};

/**
 * Capitalizes the first letter of a string
 * Handles special cases like removing dots and extra spaces
 */
const capitalize = (str: string): string => {
  if (!str) return str;
  // Remove dots and trim
  const cleaned = str.replace(/\./g, '').trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

/**
 * Custom format parser for moment-like format strings
 */
const parseCustomFormat = (date: Date, format: string, locale: string, timezone?: string): string => {
  // Handle special case for "ddd, MMMM D, YYYY"
  if (format === 'ddd, MMMM D, YYYY') {
    const weekday = capitalize(
      date.toLocaleDateString(locale, {
        weekday: 'short',
        ...(timezone && { timeZone: timezone }),
      })
    );
    const month = capitalize(
      date.toLocaleDateString(locale, {
        month: 'long',
        ...(timezone && { timeZone: timezone }),
      })
    );
    const day = date.toLocaleDateString(locale, {
      day: 'numeric',
      ...(timezone && { timeZone: timezone }),
    });
    const year = date.toLocaleDateString(locale, {
      year: 'numeric',
      ...(timezone && { timeZone: timezone }),
    });
    return `${weekday}, ${month} ${day}, ${year}`;
  }

  // Handle "MMM Do, HH:mm" format
  if (format === 'MMM Do, HH:mm' || format === ' MMM Do, HH:mm') {
    const month = capitalize(
      date.toLocaleDateString(locale, {
        month: 'short',
        ...(timezone && { timeZone: timezone }),
      })
    );
    const day = date.toLocaleDateString(locale, {
      day: 'numeric',
      ...(timezone && { timeZone: timezone }),
    });
    const time = date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      ...(timezone && { timeZone: timezone }),
    });
    return `${month} ${day}, ${time}`;
  }

  // Default: try to use predefined format
  const options = MOMENT_FORMAT_TOKENS[format];
  if (options) {
    return date.toLocaleString(locale, {
      ...options,
      ...(timezone && { timeZone: timezone }),
    });
  }

  // Fallback to ISO string
  return date.toISOString();
};

/**
 * Formats a date using native Intl.DateTimeFormat with locale support
 * Replaces moment's .locale().format() pattern
 *
 * @example
 * // Format with custom moment-like format
 * formatDateTime({
 *   date: new Date(),
 *   locale: 'es',
 *   format: 'ddd, MMMM D, YYYY'
 * })
 * // Returns: "lun, enero 29, 2026"
 *
 * @example
 * // Format with timezone
 * formatDateTime({
 *   date: 1706515200,
 *   locale: 'en',
 *   format: 'LLL',
 *   timezone: 'Europe/Madrid'
 * })
 * // Returns: "January 29, 2024, 10:00 AM"
 */
export const formatDateTime = ({ date, locale, format, timezone }: FormatDateTimeOptions): string => {
  // Convert Unix timestamp to Date if needed
  const dateObj = typeof date === 'number' ? fromUnixTimestamp(date) : date;

  // Use custom parser for complex formats
  return parseCustomFormat(dateObj, format, locale, timezone);
};
